import React, { useEffect, useState } from "react";

/**
 * A single toast notification — presentational only. State/stacking lives in
 * `src/contexts/ToastContext.js`. The upcoming container renders one of these
 * per toast in the array.
 *
 * @param {object}      props
 * @param {string|number} props.id        Unique toast id (from ToastContext).
 * @param {"success"|"error"|"warning"|"info"} props.type  Color + icon selector.
 * @param {string}      props.message   Toast body copy.
 * @param {number}      props.duration  Auto-dismiss delay in ms.
 * @param {(id) => void} props.onDismiss Dismiss handler — fired on timeout or × click.
 */

// 4px left border + icon tint per type. Tailwind palette matches the existing
// success/error/warning/info colors used by NotificationContext/StateMessage.
const TYPE_STYLES = {
  success: {
    border: "border-l-green-500",
    icon: "text-green-500 dark:text-green-400",
  },
  error: {
    border: "border-l-red-500",
    icon: "text-red-500 dark:text-red-400",
  },
  warning: {
    border: "border-l-amber-500",
    icon: "text-amber-500 dark:text-amber-400",
  },
  info: {
    border: "border-l-blue-500",
    icon: "text-blue-500 dark:text-blue-400",
  },
};

// Inline SVG type icons — same viewBox/stroke conventions as InfoTip.
const TypeIcon = ({ type }) => {
  switch (type) {
    case "success":
      return (
        <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "error":
      return (
        <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case "warning":
      return (
        <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case "info":
    default:
      return (
        <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
  }
};

const Toast = ({ id, type = "info", message, duration, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  // Slide in from the bottom on mount: start hidden (translate-y + opacity 0),
  // then flip to visible next tick so the CSS transition runs.
  useEffect(() => {
    setVisible(true);
  }, []);

  // Auto-dismiss after `duration` ms. Cleared on unmount so a manual dismiss
  // (or parent unmount) can't fire a stale callback.
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(id);
    }, duration);
    return () => clearTimeout(timer);
  }, [id, duration, onDismiss]);

  const styles = TYPE_STYLES[type] || TYPE_STYLES.info;

  return (
    <div
      role="alert"
      className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 border-l-4 ${styles.border} transition-all duration-300 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      <span className={styles.icon}>
        <TypeIcon type={type} />
      </span>
      <p className="flex-1 text-sm text-gray-800 dark:text-gray-100">{message}</p>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-200 transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default Toast;
